import {
  addDays,
  addMonths,
  dateRangeInclusive,
  endOfMonth,
  endOfWeek,
  mergeTimeBands,
  mergeTopApps,
  startOfMonth,
  startOfWeek,
} from "../dateNav";
import type { HeatmapResult } from "../habits/heatmap";
import type { InsightWindowResult } from "../insights/insightScoring";
import {
  aggregateHabitHourlyProfile,
  concatAppHourly,
  hourlyForAppEntity,
  shapeDaySeries,
  shapeWeekSeries,
} from "./focusSeriesShape";
import type { MarketingDemoDataset, MarketingDemoDaySnapshot } from "./types";

type Comparison = {
  previousDate?: string;
  previousTotalScreenTimeSeconds?: number;
  deltaSeconds?: number;
  previousTotalOpens?: number;
  deltaOpens?: number;
  direction: "up" | "down" | "flat" | "unavailable";
  opensDirection?: "up" | "down" | "flat" | "unavailable";
};

function comparisonFromTotals(
  currentTotal: number | null | undefined,
  previousTotal: number | null | undefined,
  previousDate?: string,
  currentOpens?: number | null,
  previousOpens?: number | null,
): Comparison {
  const delta = currentTotal != null && previousTotal != null ? currentTotal - previousTotal : undefined;
  const deltaOpens = currentOpens != null && previousOpens != null ? currentOpens - previousOpens : undefined;
  const direction = delta === undefined
    ? "unavailable" as const
    : Math.abs(delta) < 300
      ? "flat" as const
      : delta > 0
        ? "up" as const
        : "down" as const;
  const opensDirection = deltaOpens === undefined
    ? "unavailable" as const
    : Math.abs(deltaOpens) < 3
      ? "flat" as const
      : deltaOpens > 0
        ? "up" as const
        : "down" as const;
  return {
    previousDate,
    previousTotalScreenTimeSeconds: previousTotal ?? undefined,
    deltaSeconds: delta,
    previousTotalOpens: previousOpens ?? undefined,
    deltaOpens,
    direction,
    opensDirection,
  };
}

function snapshotOverview(day: MarketingDemoDaySnapshot) {
  return {
    date: day.date,
    totalScreenTimeSeconds: day.totalScreenTimeSeconds,
    estimatedUnlockCount: day.estimatedUnlockCount,
    permissionStatus: "granted" as const,
    collectionStatus: "success" as const,
    source: "ios" as const,
    topApps: day.topApps.map((app) => ({
      appIdentifier: app.appIdentifier,
      displayName: app.displayName,
      durationSeconds: app.durationSeconds,
      category: app.category,
      opens: app.opens,
      hourly: app.hourly,
    })),
    timeBands: day.timeBands,
    capturedAt: Date.parse(`${day.date}T12:00:00`),
  };
}

function daysInRange(dataset: MarketingDemoDataset, startDate: string, endDate: string) {
  return dataset.days.filter((day) => day.date >= startDate && day.date <= endDate);
}

function overlayGetOverview(
  args: { date: string },
  dataset: MarketingDemoDataset,
) {
  const today = dataset.byDate.get(args.date) ?? null;
  const previous = dataset.byDate.get(addDays(args.date, -1)) ?? null;
  return {
    today: today ? snapshotOverview(today) : null,
    comparison: comparisonFromTotals(
      today?.totalScreenTimeSeconds,
      previous?.totalScreenTimeSeconds,
      previous?.date,
      today?.estimatedUnlockCount,
      previous?.estimatedUnlockCount,
    ),
  };
}

function overlayGetWeekOverview(
  args: { anchorDate: string },
  dataset: MarketingDemoDataset,
) {
  const weekStart = startOfWeek(args.anchorDate);
  const weekEnd = endOfWeek(args.anchorDate);
  const snapshots = daysInRange(dataset, weekStart, weekEnd);
  const previousWeekStart = addDays(weekStart, -7);
  const previousWeekEnd = addDays(weekEnd, -7);
  const previousSnapshots = daysInRange(dataset, previousWeekStart, previousWeekEnd);

  const totalScreenTimeSeconds = snapshots.reduce((sum, day) => sum + day.totalScreenTimeSeconds, 0);
  const previousTotal = previousSnapshots.reduce((sum, day) => sum + day.totalScreenTimeSeconds, 0);
  const estimatedUnlockCount = snapshots.reduce((sum, day) => sum + day.estimatedUnlockCount, 0);
  const previousEstimatedUnlockCount = previousSnapshots.reduce((sum, day) => sum + day.estimatedUnlockCount, 0);

  return {
    week: {
      weekStart,
      weekEnd,
      anchorDate: args.anchorDate,
      totalScreenTimeSeconds,
      estimatedUnlockCount,
      permissionStatus: "granted" as const,
      collectionStatus: "success" as const,
      source: "ios" as const,
      topApps: mergeTopApps(snapshots.map((day) => day.topApps)),
      timeBands: mergeTimeBands(snapshots.map((day) => day.timeBands)),
      daysWithData: snapshots.length,
      capturedAt: Date.parse(`${args.anchorDate}T12:00:00`),
    },
    comparison: comparisonFromTotals(
      snapshots.length > 0 ? totalScreenTimeSeconds : null,
      previousSnapshots.length > 0 ? previousTotal : null,
      previousWeekStart,
      estimatedUnlockCount,
      previousEstimatedUnlockCount,
    ),
  };
}

function overlayGetMonthOverview(
  args: { anchorDate: string },
  dataset: MarketingDemoDataset,
) {
  const monthStart = startOfMonth(args.anchorDate);
  const monthEnd = endOfMonth(args.anchorDate);
  const snapshots = daysInRange(dataset, monthStart, monthEnd);
  const previousMonthStart = startOfMonth(addMonths(monthStart, -1));
  const previousMonthEnd = endOfMonth(previousMonthStart);
  const previousSnapshots = daysInRange(dataset, previousMonthStart, previousMonthEnd);

  const totalScreenTimeSeconds = snapshots.reduce((sum, day) => sum + day.totalScreenTimeSeconds, 0);
  const previousTotal = previousSnapshots.reduce((sum, day) => sum + day.totalScreenTimeSeconds, 0);
  const estimatedUnlockCount = snapshots.reduce((sum, day) => sum + day.estimatedUnlockCount, 0);
  const previousEstimatedUnlockCount = previousSnapshots.reduce((sum, day) => sum + day.estimatedUnlockCount, 0);

  return {
    month: {
      monthStart,
      monthEnd,
      anchorDate: args.anchorDate,
      totalScreenTimeSeconds,
      estimatedUnlockCount,
      permissionStatus: "granted" as const,
      collectionStatus: "success" as const,
      source: "ios" as const,
      topApps: mergeTopApps(snapshots.map((day) => day.topApps)),
      timeBands: mergeTimeBands(snapshots.map((day) => day.timeBands)),
      daysWithData: snapshots.length,
      capturedAt: Date.parse(`${args.anchorDate}T12:00:00`),
    },
    comparison: comparisonFromTotals(
      snapshots.length > 0 ? totalScreenTimeSeconds : null,
      previousSnapshots.length > 0 ? previousTotal : null,
      previousMonthStart,
      estimatedUnlockCount,
      previousEstimatedUnlockCount,
    ),
  };
}

function overlayGetHeatmap(
  args: { startDate: string; endDate: string },
  dataset: MarketingDemoDataset,
): HeatmapResult {
  return {
    days: daysInRange(dataset, args.startDate, args.endDate).map((day) => ({
      date: day.date,
      apps: day.topApps.map((app) => ({
        appIdentifier: app.appIdentifier,
        displayName: app.displayName,
        category: app.category,
        durationSeconds: app.durationSeconds,
        opens: app.opens,
      })),
    })),
  };
}

function overlayGetRecentWindow(
  args: { startDate: string; endDate: string },
  dataset: MarketingDemoDataset,
): InsightWindowResult {
  const days = daysInRange(dataset, args.startDate, args.endDate).map((day) => ({
    date: day.date,
    totalScreenTimeSeconds: day.totalScreenTimeSeconds,
    permissionStatus: "granted" as const,
    collectionStatus: "success" as const,
    topApps: day.topApps.map((app) => ({
      appIdentifier: app.appIdentifier,
      displayName: app.displayName,
      durationSeconds: app.durationSeconds,
      category: app.category,
      opens: app.opens,
      hourly: app.hourly,
    })),
    timeBands: day.timeBands,
  }));

  return {
    window: {
      startDate: args.startDate,
      endDate: args.endDate,
      daysRequested: days.length,
    },
    permissionStatus: "granted",
    days,
  };
}

function overlayGetAppFocusSeries(
  args: {
    appIdentifier?: string;
    displayName: string;
    date: string;
    mode: "day" | "week" | "month";
    isCategory?: boolean;
  },
  dataset: MarketingDemoDataset,
) {
  if (args.mode === "day") {
    const day = dataset.byDate.get(args.date);
    const topApps = day?.topApps ?? [];
    const hourly = hourlyForAppEntity(topApps, {
      appIdentifier: args.appIdentifier,
      displayName: args.displayName,
      isCategory: args.isCategory,
    });
    const { points, hasOpens } = shapeDaySeries(hourly);
    return { mode: "day" as const, points, hasOpens };
  }

  const periodDates = args.mode === "month"
    ? dateRangeInclusive(startOfMonth(args.date), endOfMonth(args.date))
    : dateRangeInclusive(startOfWeek(args.date), endOfWeek(args.date));

  const seriesDays = periodDates.map((date) => {
    const day = dataset.byDate.get(date);
    const topApps = day?.topApps ?? [];
    if (args.isCategory) {
      const seconds = topApps
        .filter((app) => app.category === args.displayName)
        .reduce((sum, app) => sum + app.durationSeconds, 0);
      const opens = topApps
        .filter((app) => app.category === args.displayName)
        .reduce((sum, app) => sum + app.opens, 0);
      return { date, seconds, opens };
    }
    const targetKey = args.appIdentifier ?? args.displayName;
    const app = topApps.find((row) => (row.appIdentifier ?? row.displayName) === targetKey);
    return {
      date,
      seconds: app?.durationSeconds ?? 0,
      opens: app?.opens ?? null,
    };
  });

  const { points, hasOpens } = shapeWeekSeries(seriesDays);
  return { mode: args.mode, points, hasOpens };
}

function overlayGetDeviceFocusSeries(
  args: { date: string; mode: "day" | "week" | "month" },
  dataset: MarketingDemoDataset,
) {
  if (args.mode === "day") {
    const day = dataset.byDate.get(args.date);
    const { points, hasOpens } = shapeDaySeries(concatAppHourly(day?.topApps ?? []));
    return { mode: "day" as const, points, hasOpens };
  }

  const periodDates = args.mode === "month"
    ? dateRangeInclusive(startOfMonth(args.date), endOfMonth(args.date))
    : dateRangeInclusive(startOfWeek(args.date), endOfWeek(args.date));

  const seriesDays = periodDates.map((date) => {
    const day = dataset.byDate.get(date);
    return {
      date,
      seconds: day?.totalScreenTimeSeconds ?? 0,
      opens: day?.estimatedUnlockCount ?? null,
    };
  });
  const { points, hasOpens } = shapeWeekSeries(seriesDays);
  return { mode: args.mode, points, hasOpens };
}

function overlayGetAppHabitHourlyProfile(
  args: {
    appIdentifier?: string;
    displayName: string;
    isCategory?: boolean;
    startDate: string;
    endDate: string;
  },
  dataset: MarketingDemoDataset,
) {
  const dailyHourlies = daysInRange(dataset, args.startDate, args.endDate).map((day) =>
    hourlyForAppEntity(day.topApps, {
      appIdentifier: args.appIdentifier,
      displayName: args.displayName,
      isCategory: args.isCategory,
    }),
  );
  return aggregateHabitHourlyProfile(dailyHourlies);
}

export type MarketingDemoQueryName =
  | "usage:getOverview"
  | "usage:getWeekOverview"
  | "usage:getMonthOverview"
  | "usage:getEarliestDataDate"
  | "usageHeatmap:getHeatmap"
  | "usageInsights:getRecentWindow"
  | "usage:getAppFocusSeries"
  | "usage:getDeviceFocusSeries"
  | "usage:getAppHabitHourlyProfile";

export function applyMarketingDemoOverlay<T>(
  queryName: MarketingDemoQueryName,
  args: Record<string, unknown>,
  raw: T | undefined,
  dataset: MarketingDemoDataset,
): T | undefined {
  switch (queryName) {
    case "usage:getOverview":
      return overlayGetOverview(args as { date: string }, dataset) as T;
    case "usage:getWeekOverview":
      return overlayGetWeekOverview(args as { anchorDate: string }, dataset) as T;
    case "usage:getMonthOverview":
      return overlayGetMonthOverview(args as { anchorDate: string }, dataset) as T;
    case "usage:getEarliestDataDate":
      return { earliestDataDate: dataset.startDate } as T;
    case "usageHeatmap:getHeatmap":
      return overlayGetHeatmap(args as { startDate: string; endDate: string }, dataset) as T;
    case "usageInsights:getRecentWindow":
      return overlayGetRecentWindow(args as { startDate: string; endDate: string }, dataset) as T;
    case "usage:getAppFocusSeries":
      return overlayGetAppFocusSeries(
        args as {
          appIdentifier?: string;
          displayName: string;
          date: string;
          mode: "day" | "week" | "month";
          isCategory?: boolean;
        },
        dataset,
      ) as T;
    case "usage:getDeviceFocusSeries":
      return overlayGetDeviceFocusSeries(
        args as { date: string; mode: "day" | "week" | "month" },
        dataset,
      ) as T;
    case "usage:getAppHabitHourlyProfile":
      return overlayGetAppHabitHourlyProfile(
        args as {
          appIdentifier?: string;
          displayName: string;
          isCategory?: boolean;
          startDate: string;
          endDate: string;
        },
        dataset,
      ) as T;
    default:
      return raw;
  }
}
